import api from "./api";

// UML diagrams for a process (use-case, activity), rendered by the backend.
// The API sends `Cross-Origin-Resource-Policy: same-origin`, so the image URL
// can't go straight into <img src>; fetch it as a blob and show a blob URL.

const MESSAGES = {
  400: "Invalid request.",
  403: "You do not have access to this process.",
  404: "Process not found.",
  422: "This process has no tasks yet, so there is no diagram to show.",
};

export function diagramErrorMessage(error) {
  return (
    MESSAGES[error?.status] ||
    "The diagram could not be generated. Please try again."
  );
}

const ACCEPT = {
  svg: "image/svg+xml",
  png: "image/png",
  report: "text/markdown",
  json: "application/json",
};

// GET `${basePath}/process/:id[/suffix]`. suffix: "svg", "png", "report" or
// "" for the JSON model.
export async function fetchProcessDiagram(basePath, processId, suffix) {
  if (!processId) throw new Error("Process ID is required");
  const endpoint = `${basePath}/process/${encodeURIComponent(processId)}${
    suffix ? `/${suffix}` : ""
  }`;
  try {
    // The page shows 400/403/404/422 inline, so keep the global error page out
    // of it; an expired session (401) still goes to the app-wide handler.
    return await api.request(endpoint, {
      method: "GET",
      headers: { Accept: ACCEPT[suffix || "json"] },
      ...(suffix ? { responseType: "blob" } : {}),
      suppressErrorPage: true,
    });
  } catch (error) {
    if (error?.status === 401) {
      window.dispatchEvent(
        new CustomEvent("app:http-error", {
          detail: { status: 401, message: error.message, endpoint },
        }),
      );
    }
    throw error;
  }
}

export function saveBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a tick to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// format: "svg" (sharp, zoomable, best on screen) or "png" (2x resolution)
export function getUseCaseDiagram(processId, format = "svg") {
  return fetchProcessDiagram("/usecase-diagram", processId, format);
}

export async function downloadUseCaseDiagram(processId, format, fileName) {
  const blob = await getUseCaseDiagram(processId, format);
  saveBlob(blob, fileName || `usecase-diagram-${processId}.${format}`);
}
